import React, { useMemo, useState } from 'react';
import { Segmented } from 'antd';
import { useTranslation } from 'react-i18next';
import { DriverActivityRuleTemplate, DriverActivityRuleText } from '../graphql/types';
import { tokenizeRuleText, variableLabel } from '../utils/ruleTextUtils';

interface RuleTextViewProps {
    ruleText: DriverActivityRuleText[];
    template: DriverActivityRuleTemplate;
    /** 右侧额外按钮（预览） */
    extra?: React.ReactNode;
}

/** 文案里的 {{key}} 渲染成紫色 chip；变量名按文案语种显示（不是界面语言），英文文案里不混中文 chip */
export const RuleTextContent: React.FC<{ text: string; lang: string }> = ({ text, lang }) => {
    const { i18n } = useTranslation();
    const t = useMemo(() => i18n.getFixedT(lang), [i18n, lang]);
    return (
        <>
            {tokenizeRuleText(text).map((token, i) =>
                token.type === 'variable' ? (
                    <span key={i} className="inline-block bg-[#F3EEFE] text-[#722ed1] rounded px-[6px] text-[12.5px] leading-5 mx-[2px]">
                        {variableLabel(t, token.value)}
                    </span>
                ) : (
                    <React.Fragment key={i}>{token.value}</React.Fragment>
                )
            )}
        </>
    );
};

/** 详情页：三语切换只读展示模板原文 */
const RuleTextView: React.FC<RuleTextViewProps> = ({ ruleText, template, extra }) => {
    const { t, i18n } = useTranslation();
    const [lang, setLang] = useState(template.languages[0]);
    // 段标题跟文案语种走；语种 tab 本身仍用界面语言
    const tl = useMemo(() => i18n.getFixedT(lang), [i18n, lang]);
    const current = ruleText.find((item) => item.language === lang);
    const contentOf = (key: string) => current?.sections.find((s) => s.key === key)?.content ?? '';
    const langOptions = template.languages.map((code) => ({
        value: code,
        label: t(`driverOps.activity.languages.${code}`, { defaultValue: code }),
    }));

    return (
        <div>
            <div className="flex items-center gap-3 mb-[10px]">
                <Segmented size="small" options={langOptions} value={lang} onChange={(v) => setLang(String(v))} />
                <span className="ml-auto">{extra}</span>
            </div>
            <div className="border border-[#D9D9D9] rounded-[6px] overflow-hidden">
                {template.sections.map((section, index) => (
                    <div key={section.key} className={`flex ${index === template.sections.length - 1 ? '' : 'border-b border-[#EFEFEF]'}`}>
                        <div className="w-[132px] shrink-0 bg-[#FAFAFA] px-3 py-[10px] font-semibold text-[13.5px] text-[#1F1F1F] border-r border-[#EFEFEF] break-words">
                            {tl(`driverOps.activity.sections.${section.key}`, { defaultValue: section.key })}
                        </div>
                        <div className="flex-1 min-w-0 px-3 py-[10px] text-[13.5px] leading-[1.8] text-[#1F1F1F] whitespace-pre-wrap break-words">
                            {contentOf(section.key) ? <RuleTextContent text={contentOf(section.key)} lang={lang} /> : <span className="text-[#BFBFBF]">--</span>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default RuleTextView;
